<template>
  <!-- Main List Modal -->
  <custom-modal modal-width="35rem" body-padding="0 24px 0px" @close="closeListModal">
    <template #header> Permissions </template>
    <template #body>
      <div class="list-header">
        <custom-button button-class="btn btn-primary padding" @click="openAddMember('user')">
          Add Member
        </custom-button>
        <custom-button button-class="btn btn-primary padding" @click="openAddMember('team')"> Add Team </custom-button>
      </div>

      <div v-if="loading" class="loading-indicator">Loading...</div>
      <div v-else>
        <!-- Members Section -->
        <div class="list-section">
          <div v-if="!membersList?.length" class="empty-state">No permissions Assigned.</div>
          <div v-else>
            <div class="list-item list-header-row">
              <div class="item-info header">Name</div>
              <div class="item-actions">
                <span class="heading">Role</span>
                <span class="heading">Edit</span>
                <span class="heading">Delete</span>
              </div>
            </div>
            <div class="list-items">
              <div v-for="member in membersList" :key="`${member.type}-${member.id}`" class="list-item">
                <div class="item-info">
                  <i :class="member.type === 'user' ? 'icon-account' : 'icon-users'"></i>
                  <span>{{ member.name }}</span>
                </div>
                <div class="item-actions">
                  <i :class="getRoleIcon(member.role)"></i>
                  <span>{{ member.role }}</span>
                  <custom-button button-class="btn btn-default btn-sm" @click="editMember(member)">
                    Edit
                  </custom-button>
                  <custom-button button-class="btn btn-default btn-sm" @click="deletePermission(member)">
                    Delete
                  </custom-button>
                  <custom-button button-class="btn btn-default btn-sm" @click="resendInvitation(member)">
                    Resend
                  </custom-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <span></span>
    </template>
  </custom-modal>

  <!-- Add/Edit Member Modal -->
  <custom-modal v-if="showDialog" modal-width="25rem" body-padding="0 24px 0px" @close="closeModal">
    <template #header>
      {{ selectedType === "team" ? "Add Team" : "Add Member" }}
    </template>
    <template #body>
      <form @submit.prevent="submit">
        <custom-input
          :value="email"
          :type="selectedType === 'team' ? 'text' : 'email'"
          :label="selectedType === 'team' ? 'Team ID' : 'Member Email'"
          :mode="editMode"
          @input="email = $event.toLowerCase()"
        ></custom-input>
        <div v-if="selectedType === 'team' && teamName" class="team-name-hint">
          Team: <span class="hint">{{ teamName }}</span>
        </div>
        <span class="user-role">Role</span>
        <select-box :value="role" :options="availableRoles" @input="role = $event"> </select-box>
        <div class="kk-memberdialog__btns">
          <custom-button button-class="btn btn-default btn-primary" button-type="submit">
            {{ editMode ? "Update" : "Add" }}
          </custom-button>
          <custom-button button-class="btn btn-default" button-type="button" @click="closeModal">
            Cancel
          </custom-button>
        </div>
      </form>
    </template>
    <template #footer>
      <span></span>
    </template>
  </custom-modal>
</template>

<script>
import CustomButton from "../../commons/CustomButton.vue";
import CustomModal from "../../commons/CustomModal.vue";
import CustomInput from "../../commons/CustomInput.vue";
import SelectBox from "../../commons/SelectBox.vue";
import DesignerMixin from "../DesignerMixin";
import { SubNavigations, permissionLevels } from "../../../utils/constants";
import { authorizationHelper } from "../../../store/helpers/AuthorizationHelper";

export default {
  components: {
    CustomButton,
    CustomModal,
    CustomInput,
    SelectBox,
  },
  mixins: [DesignerMixin],
  data() {
    return {
      showDialog: false,
      email: "",
      role: "VIEWER",
      currentPermission: "VIEWER",
      roleItems: [
        { text: "VIEWER", value: "VIEWER", icon: "icon-account-eye" },
        { text: "COMMENTOR", value: "COMMENTOR", icon: "icon-comment-edit-outline" },
        { text: "EDITOR", value: "EDITOR", icon: "icon-account-edit" },
        { text: "MANAGER", value: "MANAGER", icon: "icon-share-2" },
        { text: "ADMIN", value: "ADMIN", icon: "icon-account-cog" },
      ],
      editMode: false,
      selectedType: null,
      selectedId: null,
      membersList: [],
      loading: true,
      teamName: "",
    };
  },
  computed: {
    activeSubNavigation() {
      return this.$store.state.preferences.allPreferences?.activeSubNavigation;
    },
    selectedDesignId() {
      switch (this.activeSubNavigation) {
        case SubNavigations.Processes:
          return this.$store.state.processes?.activeProcess?.itemId;

        case SubNavigations.Grids:
          return this.$store.state.grids?.activeGrid?.itemId;

        case SubNavigations.Businesses:
          return this.$store.state.businesses?.activeBusiness?.itemId;

        default:
          return SubNavigations.Processes; // Handle unknown sub-navigation gracefully
      }
    },
    basePath() {
      return this.$store.getters["preferences/basePath"];
    },
    itemId() {
      return this.$store.getters["processes/teamId"](this.$store.state.processes.activeProcess.id);
    },
    members() {
      return this.$store.getters["teams/getItemById"](this.itemId)?.children;
    },
    availableRoles() {
      const isExistingMember = this.members?.some((member) => member.email === this.email);
      if ((!isExistingMember || this.selectedType === "team") && this.basePath !== "grids") {
        return this.roleItems.filter((role) => role.value === "VIEWER" || role.value === "COMMENTOR");
      }
      return this.roleItems.filter((role) => permissionLevels[role.value] <= permissionLevels[this.currentPermission]);
    },
  },
  watch: {
    email(newId) {
      if (this.selectedType === "team" && newId) {
        this.fetchTeamName(newId);
      } else if (newId) {
        this.teamName = newId;
      }
    },
  },
  mounted() {
    this.initialize();
  },
  methods: {
    async fetchTeamName(teamId) {
      try {
        const team = await this.$store.dispatch("teams/getTeamById", teamId);
        this.teamName = team?.name ?? "No team Found";
      } catch (error) {
        console.error("Error fetching team name:", error);
        this.teamName = "";
      }
    },
    async initialize() {
      this.loading = true;
      try {
        const permissionHelper = authorizationHelper(this.$store.dispatch, this.$store.state);

        const design = await this.$store.dispatch(`pdesigns/getPermissions`, this.selectedDesignId);
        const permossions = await permissionHelper.getProcessBasedPermission(this.selectedDesignId);
        this.currentPermission = permossions.role;
        this.membersList = design.data.permissions;
      } catch (error) {
        console.error("Error fetching permissions:", error);
      } finally {
        this.loading = false;
      }
    },
    getRoleIcon(role) {
      const icons = {
        VIEWER: "icon-account-eye",
        EDITOR: "icon-account-edit",
        COMMENTOR: "icon-comment-edit-outline",
        MANAGE: "icon-share-2",
        ADMIN: "icon-account-cog",
      };
      return icons[role] || "icon-account";
    },
    openAddMember(type) {
      this.selectedType = type;
      this.editMode = false;
      this.email = "";
      this.role = "VIEWER";
      this.showDialog = true;
    },
    editMember(member) {
      this.selectedId = member.id;
      this.selectedType = member.type;
      this.editMode = true;
      this.email = member.id;
      const isExistingMember = this.members?.some((m) => m.email === member.id);
      this.role = isExistingMember ? member.role.toLowerCase() : "viewer";
      this.showDialog = true;
    },
    deletePermission(member) {
      this.$store.dispatch("panel/setDeleteDialog", {
        ids: member.id,
        show: true,
        title: "Delete Permission",
        description: "Are you sure you want to delete this permission?",
        itemType: "permission",
        action: () => {
          this.membersList = this.membersList.filter((m) => !(m.id === member.id && m.type === member.type));
          this.updatePermission(this.selectedDesignId, this.membersList);
        },
      });
    },
    updatePermission(designId, permissions) {
      this.$store.dispatch(`${this.basePath}/syncPermissions`, {
        designId,
        updatedPermissions: permissions,
        basePath: this.basePath,
      });
    },
    async resendInvitation(member) {
      try {
        this.$store.dispatch(`${this.basePath}/resendInvitation`, {
          designId: this.selectedDesignId,
          nodeId: member.id,
        });
      } catch (error) {
        console.error("Error resending invitation:", error);
      }
    },
    closeModal() {
      this.showDialog = false;
      this.email = "";
      this.role = "VIEWER";
    },
    closeListModal() {
      this.$store.dispatch(`pdesign/setSelectedItemId`, null);
    },
    submit() {
      if (this.selectedType === "team" && !this.email.trim()) {
        alert("Please provide a valid team ID.");
        return;
      } else if (this.selectedType === "user" && !/\S+@\S+\.\S+/.test(this.email)) {
        alert("Please provide a valid email address.");
        return;
      }

      const roleValue = this.role.charAt(0).toUpperCase() + this.role.slice(1);

      // Check for duplicate ID
      const isDuplicate = this.membersList.some((member) => member.id.toLowerCase() === this.email.toLowerCase());

      if (isDuplicate && !this.editMode) {
        alert("This ID already has access you can resend the invitation.");
        return;
      }

      if (this.editMode) {
        // Update the existing member
        this.membersList = this.membersList.map((member) =>
          member.id === this.selectedId ? { ...member, role: roleValue, name: this.teamName } : member
        );
      } else {
        // Add a new member
        this.membersList.push({
          type: this.selectedType,
          id: this.email.toLowerCase(),
          role: roleValue,
          name: this.teamName,
        });
      }

      // Update permissions and close the modal
      this.updatePermission(this.selectedDesignId, this.membersList);
      this.closeModal();
    },
  },
};
</script>

<style lang="less" scoped>
.list-header {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  .padding {
    padding: 8px 12px;
  }
}

.list-section {
  margin-bottom: 24px;

  h3 {
    margin-bottom: 16px;
    font-weight: 500;
  }
}

.list-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f5f5;
  border-radius: 4px;
}

.item-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-actions {
  align-items: center;
  gap: 12px;
}

.user-role {
  display: block;
  margin-top: 8px;
}

.kk-memberdialog__btns {
  display: flex;
  gap: 10px;
  padding-top: 1rem;
  justify-content: right;
}
.list-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #e0e0e0;
  font-weight: bold;
  padding: 12px 16px;
  border-bottom: 1px solid #ccc;

  .item-actions .heading {
    text-align: center; /* Center align action headers like Edit and Delete */
  }

  .item-actions {
    flex: 2;
  }
}
.btn-sm {
  padding: 4px 8px !important;
  font-size: 12px !important;
  min-width: auto;
}

.list-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f5f5;
  border-radius: 4px;

  .item-info {
    flex: 2; /* Adjust width ratio for the name column */
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .item-actions {
    /* Adjust width ratio for the actions */
    display: flex;
    align-items: center;
    justify-content: space-around; /* Evenly distribute action items */
    gap: 12px;
  }
  .team-name-hint {
    margin-top: 8px;
    font-size: 0.9rem;
    color: #555;
  }

  .team-name-hint .hint {
    font-weight: bold;
    color: #000;
  }
}
</style>
